import React, { useEffect } from "react";
// Components
import { BrandAdContainer } from "web/react/components/LEGACY-brand-ad-container/brand-ad-container";
import RelatedProductsArea from "web/react/components/LEGACY_related-products-area/related-products-area";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import { AppFooter } from "web/react/components/app-footer/app-footer";
import Breadcrumbs from "web/react/components/breadcrumbs/breadcrumbs";
import ProductDescription from "web/react/components/buybuybuy-area/product-description/product-description";
import Conditional from "web/react/components/conditional/conditional";
import CuratedAds from "web/react/components/curated-ads/curated-ads";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import OOSProductHero from "web/react/components/oos-product-hero/oos-product-hero";
import OOSProductHeroLegacy from "web/react/components/oos-product-hero/oos-product-hero-legacy";
import PDPGallery from "web/react/components/pdp-gallery/pdp-gallery";
import ProductRetailers from "web/react/components/product-retailers/product-retailers";
import RecentlyViewed from "web/react/components/recently-viewed/recently-viewed";
import RelatedDesignersRail from "web/react/components/related-designers/related-designers-rail";
import RelatedProductsRail from "web/react/components/related-products-rail/related-products-rail";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
// Styles
// eslint-disable-next-line css-modules/no-unused-class
import styles from "web/react/pages/product/out-of-stock/out-of-stock-product-page/out-of-stock-product-page.module.css";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import {
    OOSAffiliateProductLayoutSerializer,
    OOSIconProductLayoutSerializer,
} from "web/types/serializers";

interface OutOfStockProductPageProps {
    productLayout: OOSAffiliateProductLayoutSerializer | OOSIconProductLayoutSerializer;
    productUrl: string;
    helpCentreURLs: string[];
    showCookieSettingsOption: boolean;
    country: string;
    variant: "affiliate" | "icon";
    labels?: Record<string, any>;
    linkId?: string;
    similarAIRelatedSearches?: any[];
}

function OutOfStockProductPage({
    productLayout,
    productUrl,
    helpCentreURLs,
    showCookieSettingsOption,
    country,
    variant,
    labels,
    linkId,
    similarAIRelatedSearches,
}: OutOfStockProductPageProps): React.ReactElement {
    const {
        product_analytics_data: productAnalyticsData,
        brand_ad: brandAd,
        curated_ads: curatedAds,
        product_hero_card: productHeroCard,
        retailer_data: retailerData,
        adsense_for_shopping: adsenseForShopping,
        related_products: relatedProducts,
        related_search_rail: relatedSearchRail,
        product_breadcrumbs: productBreadcrumbs,
        product_description: productDescription,
        product_more_descriptions: productMoreDescriptions,
        product_image_gallery: productImageGallery,
        product_retailers: productRetailers,
        recently_viewed_products: recentlyViewedProducts,
        footer,
        features,
    } = productLayout;

    const { related_designers_category_rail: relatedDesignersCategoryRail } =
        productLayout as OOSAffiliateProductLayoutSerializer;

    const isAffiliate = variant === "affiliate";
    const useNewOOSDesign =
        environment.getFeature("eac_oos_pdp_redesign") &&
        environment.getFeature("eac_oos_pdp_redesign") !== "variant_control" &&
        isAffiliate;

    // ORG-3540: Elevate the Try these instead section on OOS PDP
    const seoElevateSimilarItems = environment.getFeature("seo_elevate_similar_items_in_oos_pdp");

    useEffect(() => {
        userProfiler.saveSeenProduct(productAnalyticsData);
    }, [productAnalyticsData]);

    return (
        <div className={styles.container}>
            <CustomerCaptureOverlay />
            <div className={styles.content}>
                {/* ORG-3540: Show related products at the top for OOS PDP */}
                {seoElevateSimilarItems && (
                    <div className={styles.notAvailableSection}>
                        {/* TODO: I18N if the experiment is successful */}
                        <p>{"We're sorry - this item is temporarily unavailable."}</p>

                        {productHeroCard && seoElevateSimilarItems === "variantA" && (
                            <Conditional key="sfl-not-available-section" check={isAffiliate}>
                                <SaveForLaterButton
                                    // TODO: I18N if the experiment is successful
                                    cta="Alert me when back in stock"
                                    type="LEGACY_stockAlert"
                                    pageSource="oos"
                                    isLoggedIn={userProfiler.isLoggedIn()}
                                    productId={productHeroCard.product_header.product_id}
                                />
                            </Conditional>
                        )}
                    </div>
                )}

                {seoElevateSimilarItems && relatedProducts && (
                    <RelatedProductsRail
                        products={relatedProducts.sections[0].related_products}
                        title={relatedProducts.sections[0].title}
                    />
                )}

                {productHeroCard &&
                    (useNewOOSDesign ? (
                        <OOSProductHero
                            variant={variant}
                            sections={relatedProducts?.sections}
                            data={productHeroCard}
                            productUrl={productUrl}
                            labels={labels}
                            linkId={linkId}
                            retailerData={retailerData}
                        />
                    ) : (
                        <OOSProductHeroLegacy
                            variant={variant}
                            data={productHeroCard}
                            productUrl={productUrl}
                            labels={labels}
                        />
                    ))}

                {brandAd && <BrandAdContainer {...brandAd} />}

                {adsenseForShopping && (
                    <div className="mb20">
                        <AdsenseShopping {...adsenseForShopping} />
                    </div>
                )}

                {curatedAds && <CuratedAds {...curatedAds} usesFeedLayout={false} />}

                {!seoElevateSimilarItems && relatedProducts && (
                    <div className="mb10 mt10">
                        <RelatedProductsArea
                            sections={relatedProducts.sections}
                            noBrandAd={!!brandAd}
                            noAdsense={!!adsenseForShopping}
                            onOOS
                            isAffiliate={isAffiliate}
                            retailerSlug={retailerData?.slug}
                        />
                    </div>
                )}

                {isAffiliate && (
                    <>
                        {!!relatedDesignersCategoryRail?.designers?.length && (
                            <RelatedDesignersRail
                                title={relatedDesignersCategoryRail.title}
                                designers={relatedDesignersCategoryRail.designers}
                                features={features}
                                showImages={false}
                            />
                        )}
                    </>
                )}

                {((similarAIRelatedSearches && similarAIRelatedSearches.length) ||
                    (relatedSearchRail && !!relatedSearchRail.related_searches.length)) && (
                    <RelatedSearchesRail
                        title={gettext("product.related_products.related_searches")}
                        relatedSearches={
                            (similarAIRelatedSearches as any[]) ||
                            relatedSearchRail?.related_searches
                        }
                        features={features as any}
                    />
                )}

                {productBreadcrumbs && (
                    <Breadcrumbs type="pdp" breadcrumbs={productBreadcrumbs?.breadcrumbs} />
                )}

                {productDescription && (
                    <div className={styles.productDescription}>
                        <div className="page-block p30 pt10">
                            <ProductDescription
                                title={productDescription.title}
                                description={productDescription.description}
                                read_more_limit={productDescription.read_more_limit}
                                product_sizes={productDescription.product_sizes}
                                more_descriptions={productMoreDescriptions}
                                without_scroll
                            />
                            {productImageGallery &&
                                productImageGallery.images &&
                                !!productImageGallery.images.length && (
                                    <>
                                        <h4 className="mt20">
                                            {gettext("oos_product.product_details_photos_title")}
                                        </h4>

                                        <div className="mb0">
                                            <PDPGallery
                                                variant="oos-carousel"
                                                hasRelatedProductsSidebar={false}
                                                product={productImageGallery}
                                            />
                                        </div>
                                    </>
                                )}
                        </div>
                    </div>
                )}

                <div className="m20 mt10 mb10">
                    <ProductRetailers
                        retailers={productRetailers.retailers}
                        previously_sold_at_retailers_html={
                            productRetailers.previously_sold_at_retailers_html
                        }
                    />
                </div>

                {!seoElevateSimilarItems && (
                    <div className="mb40">
                        <RecentlyViewed
                            title={recentlyViewedProducts.title}
                            products={recentlyViewedProducts.products}
                            is_loading={recentlyViewedProducts.is_loading}
                            current_product_id={recentlyViewedProducts.current_product_id}
                            in_overlay={recentlyViewedProducts.in_overlay}
                        />
                    </div>
                )}
            </div>

            <AppFooter
                data={footer}
                helpCenterUrls={helpCentreURLs}
                showCookieSettingsOption={showCookieSettingsOption}
                country={country}
            />
        </div>
    );
}

export default OutOfStockProductPage;
